import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { UserSummary } from '../../queries/users';
import { SortDirection, sortUsers, UserSortField } from './users-helpers';
import { Badge } from '../../components/ui/Badge';
import { cn } from '../../lib/utils';

interface Column {
  key: UserSortField;
  label: string;
}

const COLUMNS: Column[] = [
  { key: 'id', label: 'User ID' },
  { key: 'first_name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone_number', label: 'Phone' },
  { key: 'subscription_level', label: 'Subscription' },
  { key: 'created_at', label: 'Registered' },
];

const DATE_FORMATTER = new Intl.DateTimeFormat('en-AU', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

// Shared classes for the mobile card ↔ desktop table reflow (mirrors DataTable).
const TD = 'flex items-center justify-between gap-4 border-b border-border/60 py-2 text-right last:border-0 before:mr-auto before:text-xs before:font-medium before:uppercase before:tracking-wide before:text-muted-foreground before:content-[attr(data-label)] @2xl:table-cell @2xl:border-0 @2xl:px-3 @2xl:py-2.5 @2xl:text-left @2xl:align-middle @2xl:before:content-none';

interface UsersTableProps {
  users: UserSummary[];
}

export function UsersTable({ users }: UsersTableProps) {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState<UserSortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (field: UserSortField) => {
    if (field === sortField) setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sorted = sortUsers(users, sortField, sortDirection);

  if (users.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border py-12 text-center text-muted-foreground">
        No users found.
      </div>
    );
  }

  return (
    <div className="@container w-full">
      <div className="@2xl:overflow-x-auto @2xl:rounded-xl @2xl:border @2xl:border-border @2xl:bg-card @2xl:shadow-sm">
        <table className="block w-full text-sm @2xl:table">
          <thead className="hidden @2xl:table-header-group">
            <tr className="border-b border-border">
              {[...COLUMNS, { key: 'marketing', label: 'Marketing' }, { key: 'weekly', label: 'Weekly Tips' }].map((col) => {
                const sortable = COLUMNS.some((c) => c.key === col.key);
                return (
                  <th
                    key={col.key}
                    onClick={sortable ? () => handleSort(col.key as UserSortField) : undefined}
                    className={cn(
                      'whitespace-nowrap bg-muted/50 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground',
                      sortable && 'cursor-pointer select-none hover:text-foreground',
                    )}
                  >
                    <span className="inline-flex items-center gap-1">
                      {col.label}
                      {sortField === col.key &&
                        (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="block @2xl:table-row-group">
            {sorted.map((user) => (
              <tr
                key={user.id}
                onClick={() => navigate(`/users/${user.id}`)}
                className="mb-3 block cursor-pointer rounded-xl border border-border bg-card p-3 shadow-sm @2xl:mb-0 @2xl:table-row @2xl:rounded-none @2xl:border-0 @2xl:border-b @2xl:border-border @2xl:bg-transparent @2xl:p-0 @2xl:shadow-none @2xl:transition-colors @2xl:last:border-0 @2xl:hover:bg-muted/40"
              >
                <td data-label="User ID" className={cn(TD, 'font-mono text-xs')}>{user.id}</td>
                <td data-label="Name" className={cn(TD, 'font-medium')}>
                  {[user.first_name, user.last_name].filter(Boolean).join(' ') || '—'}
                </td>
                <td data-label="Email" className={TD}>{user.email ?? '—'}</td>
                <td data-label="Phone" className={TD}>{user.phone_number ?? '—'}</td>
                <td data-label="Subscription" className={TD}>{user.subscription_level}</td>
                <td data-label="Registered" className={cn(TD, 'whitespace-nowrap text-muted-foreground @2xl:text-foreground')}>
                  {DATE_FORMATTER.format(new Date(user.created_at))}
                </td>
                <td data-label="Marketing" className={TD}>
                  <Badge variant={user.marketing_consent ? 'success' : 'outline'}>{user.marketing_consent ? 'Yes' : 'No'}</Badge>
                </td>
                <td data-label="Weekly Tips" className={TD}>
                  <Badge variant={user.weekly_tips_subscribed ? 'success' : 'outline'}>{user.weekly_tips_subscribed ? 'Yes' : 'No'}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
